<script setup>
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
import { Shield, Activity, FileText } from "lucide-vue-next";
import fenixHero from "@/assets/fenix-hero.png";

const { t } = useI18n();
</script>

<template>
  <section class="relative min-h-screen flex items-center bg-background cyber-grid overflow-hidden">
    <!-- Subtle gradient overlay -->
    <div class="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary/20" />
    
    <!-- Ambient glow effects -->
    <div class="absolute top-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
    <div class="absolute bottom-1/4 left-1/4 w-64 h-64 bg-accent/3 rounded-full blur-2xl" />

    <div class="container relative z-10 mx-auto px-6 pt-32 pb-20 lg:pt-40 lg:pb-32">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <!-- Left side - Content -->
        <div class="max-w-2xl">
          <!-- Headline -->
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight animate-fade-in-delay-1">
            {{ t("landing.hero.title") }}
          </h1>

          <!-- Subheadline -->
          <p class="text-lg md:text-xl text-muted-foreground mb-4 max-w-2xl animate-fade-in-delay-2">
            {{ t("landing.hero.subtitle") }}
          </p>

          <!-- Support line -->
          <p class="text-base text-muted-foreground/70 mb-10 animate-fade-in-delay-3">
            {{ t("landing.hero.support_line") }}
          </p>

          <!-- CTA Buttons -->
          <div class="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-in-delay-3">
            <a href="#contact-form">
              <Button variant="hero-primary" size="xl">
                {{ t("landing.hero.cta.start_trial") }}
              </Button>
            </a>
            <a href="#contact-form">
              <Button variant="hero-secondary" size="xl">
                {{ t("landing.hero.cta.request_demo") }}
              </Button>
            </a>
          </div>

          <!-- Bullet points -->
          <div class="flex flex-col sm:flex-row gap-6 text-sm text-muted-foreground animate-fade-in-delay-3">
            <div class="flex items-center gap-2">
              <Activity class="w-4 h-4 text-accent" />
              <span>{{ t("landing.hero.bullets.realtime") }}</span>
            </div>
            <div class="flex items-center gap-2">
              <Shield class="w-4 h-4 text-accent" />
              <span>{{ t("landing.hero.bullets.ai_risks") }}</span>
            </div>
            <div class="flex items-center gap-2">
              <FileText class="w-4 h-4 text-accent" />
              <span>{{ t("landing.hero.bullets.audit_reports") }}</span>
            </div>
          </div>
        </div>

        <!-- Right side - Hero Image -->
        <div class="hidden lg:flex justify-center items-center">
          <div class="relative">
            <!-- Glow effect behind the image -->
            <div class="absolute inset-0 bg-accent/10 rounded-full blur-3xl scale-75" />
            
            <!-- Hero image -->
            <img
              :src="fenixHero"
              :alt="t('landing.hero.image_alt')"
              class="relative w-full max-w-md xl:max-w-lg opacity-90 drop-shadow-2xl animate-fade-in-delay-2"
            />
            
            <!-- Subtle accent ring -->
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div class="w-[80%] h-[80%] rounded-full border border-accent/10" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom fade -->
    <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
  </section>
</template>
